import { Handle, Position } from "@xyflow/react";
import {
  getNodeTypeConfig,
  getCategoryFallbackIcon,
} from "../../data/topology.config";
import {
  useDeviceTypes,
  getDeviceTypeDisplay,
} from "../../context/DeviceTypesContext";
import "../../assets/styles/colors.css";

// viewMode: "icon" | "compact" | "full"  (compact is the default)
function DeviceNode({ data, selected, viewMode = "compact" }) {
  const deviceTypesMap = useDeviceTypes();
  if (!data) return null;

  const display = getDeviceTypeDisplay(
    data.nodeType,
    deviceTypesMap,
    getNodeTypeConfig,
  );
  // Icon resolution falls back to a category-based icon so nodes still render
  // an image even when the device-type lookup yields no icon.
  const icon = display?.icon || getCategoryFallbackIcon(data.category);
  const label = data.label || display?.label || "Unknown";

  // Extract main fields
  const ipAddress = data["ip_address"] || "";
  const hostname = data["hostname"] || "";
  const os = data["os"] || "";
  const category = data["category"] || "";

  // Only show these four in the card (compact mode)
  const mainFields = [
    { key: "os", value: os },
    { key: "hostname", value: hostname },
    { key: "ip_address", value: ipAddress },
    { key: "category", value: category },
  ].filter((f) => f.value);

  // All fields except label/nodeType (full mode)
  const excludedKeys = ["label", "nodeType"];
  const allFields = Object.entries(data)
    .filter(([key]) => !excludedKeys.includes(key))
    .map(([key, value]) => ({ key, value: String(value || "") }));

  const isIconOnly = viewMode === "icon";
  const fieldsToShow = viewMode === "full" ? allFields : mainFields;

  // ── ICON-ONLY MODE: large centered icon with name below, no card chrome ──
  if (isIconOnly) {
    return (
      <div
        className={`device-node flex flex-col items-center gap-1.5 bg-transparent border-0 p-1 ${
          selected ? "device-node-selected" : ""
        }`}
        title="Double-click to edit properties"
      >
        <Handle
          type="target"
          position={Position.Top}
          className="h-2 w-2 rounded-full border-2 border-(--color-primary)/50 bg-(--color-primary) transition-colors hover:bg-(--color-primary)/80"
        />

        <div
          className={`flex items-center justify-center rounded-xl p-2 ${
            selected ? "ring-2 ring-(--color-primary)" : ""
          }`}
        >
          {icon ? (
            <img
              src={icon}
              alt={label}
              className="h-14 w-14 object-contain shrink-0"
            />
          ) : (
            <div className="h-14 w-14 rounded-xl bg-(--color-bg-darkest) flex items-center justify-center text-(--color-primary) font-bold text-xl">
              {label.charAt(0).toUpperCase()}
            </div>
          )}
        </div>

        <span className="text-xs text-(--color-text-primary) font-semibold text-center max-w-30 truncate">
          {label}
        </span>

        <Handle
          type="source"
          position={Position.Bottom}
          className="h-2 w-2 rounded-full border-2 border-(--color-primary)/50 bg-(--color-primary) transition-colors hover:bg-(--color-primary)/80"
        />
      </div>
    );
  }

  // ── COMPACT / FULL MODE: card with header + fields table ──
  return (
    <div
      className={`device-node rounded-lg border border-(--color-border) bg-(--color-bg-card) dark:bg-(--color-bg-darkest) p-2 ${selected ? "device-node-selected border-(--color-primary)" : ""}`}
      title="Double-click to edit properties"
    >
      <Handle
        type="target"
        position={Position.Top}
        className="h-2 w-2 rounded-full border-2 border-(--color-primary)/50 bg-(--color-primary) transition-colors hover:bg-(--color-primary)/80"
      />

      {/* Header with icon and label */}
      <div className="flex items-center gap-2 mb-2 pb-2 border-b border-(--color-border)">
        {icon && (
          <img
            src={icon}
            alt={label}
            className="h-5 w-5 object-contain shrink-0"
          />
        )}
        <span className="text-xs text-(--color-text-primary) whitespace-nowrap font-semibold">
          {label}
        </span>
      </div>

      {/* Fields table */}
      {fieldsToShow.length > 0 ? (
        <div className="w-full">
          <table className="w-full text-xs">
            <tbody>
              {fieldsToShow.map(({ key, value }, index) => (
                <tr
                  key={key}
                  className={
                    index % 2 === 0
                      ? "bg-(--color-bg-darker)/60 dark:bg-black/20"
                      : ""
                  }
                >
                  <td className="px-1.5 py-0.5 text-(--color-primary) font-mono align-top">
                    {key.replace(/_/g, " ")}
                  </td>
                  <td className="px-1.5 py-0.5 text-(--color-text-primary) font-mono align-top wrap-break-word max-w-30">
                    {value}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ) : (
        <div className="text-xs text-(--color-text-muted) italic text-center py-1">
          No metadata
        </div>
      )}

      <Handle
        type="source"
        position={Position.Bottom}
        className="h-2 w-2 rounded-full border-2 border-(--color-primary)/50 bg-(--color-primary) transition-colors hover:bg-(--color-primary)/80"
      />
    </div>
  );
}

export default DeviceNode;
